import { faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../../components/base/Button';
import { Col, Form, Row, Alert } from 'react-bootstrap';
import { useState } from 'react';
import { useAuthContext } from '../../providers/AuthProvider';
import setAuthToken from '../../components/utils/setAuthToken';
import axios from 'axios';
import { Spinner } from 'react-bootstrap';
import { API } from '../../config/api';

const SignInForm = ({ layout }) => {
  const [state, authDispatch] = useAuthContext();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submitForm = async () => {
    setError(null);

    if (!formData.email || !formData.password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API.BACKEND}/api/v1/users/login`,
        formData
      );

      // Store the token BEFORE dispatching, not after.
      //
      // SET_TOKEN only flips React state; the token reached localStorage and
      // axios.defaults through AuthProvider's useEffect([state.token]). But
      // effects run child-first, and that effect is the PARENT's — so the
      // authenticated tree mounted and fired its requests one commit before the
      // token existed anywhere a request could read it. The navbar's alerts
      // poll went out bare, :5000 answered 401, and the global interceptor in
      // App.js wiped the token and reloaded to the login page. The dashboard
      // appeared for an instant and bounced straight back.
      //
      // A page refresh never showed it: loadUser() sets the header itself
      // before dispatching USER_LOADED, so only a fresh login was broken —
      // which is exactly the path nobody takes while a token is still in
      // localStorage from yesterday.
      setAuthToken(response.data.token);

      authDispatch({
        type: 'SET_TOKEN',
        payload: {
          token: response.data.token,
          user: response.data.user
        }
      });
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.msg ||
        'Login failed. Please try again.';
      setError(msg);
      authDispatch({ type: 'LOGIN_FAIL', payload: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') submitForm();
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-1000">Sign In</h3>
        <p className="text-700">Get access to your account</p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="email">Email address</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            id="email"
            type="email"
            className="form-icon-input"
            placeholder="name@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            onKeyDown={handleKeyDown}
          />
          <FontAwesomeIcon icon={faUser} className="text-900 fs-9 form-icon" />
        </div>
      </Form.Group>
      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="password">Password</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            id="password"
            type="password"
            className="form-icon-input"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            onKeyDown={handleKeyDown}
          />
          <FontAwesomeIcon icon={faKey} className="text-900 fs-9 form-icon" />
        </div>
      </Form.Group>
      <Row className="flex-between-center mb-7">
        <Col xs="auto">
          <Form.Check type="checkbox" className="mb-0">
            <Form.Check.Input
              type="checkbox"
              name="remember-me"
              id="remember-me"
              defaultChecked
            />
            <Form.Check.Label htmlFor="remember-me" className="mb-0">
              Remember me
            </Form.Check.Label>
          </Form.Check>
        </Col>
      </Row>
      <Button
        variant="primary"
        className="w-100 mb-3"
        onClick={submitForm}
        disabled={loading}
      >
        {loading ? <Spinner animation="border" size="sm" /> : 'Sign In'}
      </Button>
    </>
  );
};

export default SignInForm;
